// src/admin/AdminRouteRevisionsPage.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';

function AdminRouteRevisionsPage() {
  const [revisions, setRevisions] = useState([]);
  const [routes, setRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchRevisions();
  }, []);

  const fetchRevisions = async () => {
    setLoading(true);
    try {
      // Fetch all revisions
      const revisionsSnap = await getDocs(collection(db, 'routeRevisions'));
      const revisionsData = revisionsSnap.docs.map((docSnap) => ({
        ...docSnap.data(),
        id: docSnap.id
      }));

      // Fetch associated route data
      const routeIds = [...new Set(revisionsData.map(r => r.routeId))];
      const routesData = {};

      for (const routeId of routeIds) {
        const routeDoc = await getDoc(doc(db, 'routes', routeId));
        if (routeDoc.exists()) {
          routesData[routeId] = { ...routeDoc.data(), id: routeDoc.id };
        }
      }

      setRevisions(revisionsData);
      setRoutes(routesData);
    } catch (err) {
      console.error('Error fetching revisions:', err);
      setError('Failed to load revisions');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRevision = async (revisionId) => {
    if (!window.confirm('Are you sure you want to approve this revision? This will update the live route.')) {
      return;
    }

    setProcessing(true);
    try {
      const revision = revisions.find(r => r.id === revisionId);
      if (!revision) {
        alert('Revision not found');
        return;
      }

      // Update the live route with revision data
      await updateDoc(doc(db, 'routes', revision.routeId), {
        ...revision.revisionData,
        updatedAt: serverTimestamp()
      });

      // Mark revision as approved
      await updateDoc(doc(db, 'routeRevisions', revisionId), {
        status: 'approved',
        reviewedBy: auth.currentUser.uid,
        reviewedAt: serverTimestamp(),
        reviewNotes: reviewNotes || 'Approved'
      });

      alert('Revision approved successfully! The live route has been updated.');
      setShowDiff(false);
      setSelectedRevision(null);
      setReviewNotes('');
      await fetchRevisions();
    } catch (err) {
      console.error('Error approving revision:', err);
      alert('Failed to approve revision: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRevision = async (revisionId) => {
    if (!reviewNotes.trim()) {
      alert('Please provide notes explaining why this revision was rejected.');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this revision?')) {
      return;
    }

    setProcessing(true);
    try {
      await updateDoc(doc(db, 'routeRevisions', revisionId), {
        status: 'rejected',
        reviewedBy: auth.currentUser.uid,
        reviewedAt: serverTimestamp(),
        reviewNotes: reviewNotes
      });

      alert('Revision rejected. The creator will be notified.');
      setShowDiff(false);
      setSelectedRevision(null);
      setReviewNotes('');
      await fetchRevisions();
    } catch (err) {
      console.error('Error rejecting revision:', err);
      alert('Failed to reject revision: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDiff = (revision) => {
    setSelectedRevision(revision);
    setShowDiff(true);
    setReviewNotes('');
  };

  const renderFieldDiff = (fieldName, oldValue, newValue) => {
    // Handle arrays (like cities, storeBulletPoints, etc.)
    if (Array.isArray(oldValue) || Array.isArray(newValue)) {
      const oldArray = Array.isArray(oldValue) ? oldValue : [];
      const newArray = Array.isArray(newValue) ? newValue : [];

      if (JSON.stringify(oldArray) === JSON.stringify(newArray)) {
        return null; // No change
      }

      return (
        <div key={fieldName} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h4 style={{ marginTop: 0, color: '#333' }}>{fieldName}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <h5 style={{ color: '#dc3545' }}>Original:</h5>
              <pre style={{ backgroundColor: '#f8d7da', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(oldArray, null, 2)}
              </pre>
            </div>
            <div>
              <h5 style={{ color: '#28a745' }}>Revised:</h5>
              <pre style={{ backgroundColor: '#d4edda', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(newArray, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      );
    }

    // Handle simple values
    if (oldValue === newValue || ((!oldValue || oldValue === '') && (!newValue || newValue === ''))) {
      return null; // No change
    }

    return (
      <div key={fieldName} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h4 style={{ marginTop: 0, color: '#333' }}>{fieldName}</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <h5 style={{ color: '#dc3545' }}>Original:</h5>
            <div style={{ backgroundColor: '#f8d7da', padding: '10px', borderRadius: '4px', wordBreak: 'break-word' }}>
              {oldValue || <em style={{ color: '#999' }}>(empty)</em>}
            </div>
          </div>
          <div>
            <h5 style={{ color: '#28a745' }}>Revised:</h5>
            <div style={{ backgroundColor: '#d4edda', padding: '10px', borderRadius: '4px', wordBreak: 'break-word' }}>
              {newValue || <em style={{ color: '#999' }}>(empty)</em>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div>Loading revisions...</div>;
  }

  return (
    <div>
      <h2>Route Revisions</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Review and approve creator-submitted revisions to live routes
      </p>

      {/* Revisions Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
            <th style={{ padding: '12px', textAlign: 'left' }}>Route Name</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Submitted By</th>
            <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
            <th style={{ padding: '12px', textAlign: 'center' }}>Submitted</th>
            <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {revisions.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No revisions to review
              </td>
            </tr>
          ) : (
            revisions.map((revision) => {
              const route = routes[revision.routeId];
              const statusColors = {
                pending: '#f59e0b',
                approved: '#10b981',
                rejected: '#ef4444'
              };
              const statusColor = statusColors[revision.status] || '#666';

              return (
                <tr key={revision.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px' }}>
                    {route?.name || revision.revisionData?.routeName || 'Unknown Route'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {revision.creatorId?.substring(0, 8)}...
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: statusColor,
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {revision.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem' }}>
                    {revision.submittedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewDiff(revision)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Diff Modal */}
      {showDiff && selectedRevision && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#f5f5f5',
              padding: '30px',
              borderRadius: '10px',
              maxHeight: '90vh',
              overflowY: 'auto',
              width: '100%',
              maxWidth: '1200px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
              Review Revision: {routes[selectedRevision.routeId]?.name || 'Unknown Route'}
            </h3>

            <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
              <p style={{ margin: '5px 0' }}>
                <strong>Submitted by:</strong> {selectedRevision.creatorId?.substring(0, 12)}...
              </p>
              <p style={{ margin: '5px 0' }}>
                <strong>Submitted at:</strong> {selectedRevision.submittedAt?.toDate?.()?.toLocaleString() || 'N/A'}
              </p>
              <p style={{ margin: '5px 0' }}>
                <strong>Status:</strong> {selectedRevision.status}
              </p>
            </div>

            <h4 style={{ marginTop: '30px', marginBottom: '15px', color: '#333' }}>Changes:</h4>

            <div>
              {renderFieldDiff('Route Name', selectedRevision.originalData.routeName, selectedRevision.revisionData.routeName)}
              {renderFieldDiff('Selected Countries', selectedRevision.originalData.selectedCountries, selectedRevision.revisionData.selectedCountries)}
              {renderFieldDiff('Cities', selectedRevision.originalData.cities, selectedRevision.revisionData.cities)}
              {renderFieldDiff('Store Bullet Points', selectedRevision.originalData.storeBulletPoints, selectedRevision.revisionData.storeBulletPoints)}
              {renderFieldDiff('Min Duration', selectedRevision.originalData.minDuration, selectedRevision.revisionData.minDuration)}
              {renderFieldDiff('Max Duration', selectedRevision.originalData.maxDuration, selectedRevision.revisionData.maxDuration)}
              {renderFieldDiff('Best Time to Visit', selectedRevision.originalData.bestTimeToVisit, selectedRevision.revisionData.bestTimeToVisit)}
              {renderFieldDiff('Budget Estimate', selectedRevision.originalData.budgetEstimate, selectedRevision.revisionData.budgetEstimate)}
              {renderFieldDiff('Difficulty Level', selectedRevision.originalData.difficultyLevel, selectedRevision.revisionData.difficultyLevel)}
              {renderFieldDiff('Color Scheme', selectedRevision.originalData.colorScheme, selectedRevision.revisionData.colorScheme)}
              {renderFieldDiff('Story Behind Route', selectedRevision.originalData.storyBehindRoute, selectedRevision.revisionData.storyBehindRoute)}
              {renderFieldDiff('Transportation', selectedRevision.originalData.transportationBetweenCities, selectedRevision.revisionData.transportationBetweenCities)}
              {renderFieldDiff('Special Considerations', selectedRevision.originalData.specialConsiderations, selectedRevision.revisionData.specialConsiderations)}
              {renderFieldDiff('Clothing Recommendations', selectedRevision.originalData.clothingRecommendations, selectedRevision.revisionData.clothingRecommendations)}
              {renderFieldDiff('Suggested Hostels', selectedRevision.originalData.suggestedHostels, selectedRevision.revisionData.suggestedHostels)}
            </div>

            {selectedRevision.status === 'pending' && (
              <>
                <div style={{ marginTop: '30px', marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                    Review Notes (optional for approval, required for rejection):
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add notes about this revision..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontFamily: 'inherit',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                  <button
                    onClick={() => handleApproveRevision(selectedRevision.id)}
                    disabled={processing}
                    style={{
                      padding: '12px 30px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'wait' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: processing ? 0.6 : 1
                    }}
                  >
                    {processing ? 'Processing...' : 'Approve & Apply Changes'}
                  </button>

                  <button
                    onClick={() => handleRejectRevision(selectedRevision.id)}
                    disabled={processing}
                    style={{
                      padding: '12px 30px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'wait' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: processing ? 0.6 : 1
                    }}
                  >
                    {processing ? 'Processing...' : 'Reject Changes'}
                  </button>

                  <button
                    onClick={() => {
                      setShowDiff(false);
                      setSelectedRevision(null);
                      setReviewNotes('');
                    }}
                    disabled={processing}
                    style={{
                      padding: '12px 30px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'wait' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: processing ? 0.6 : 1
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}

            {selectedRevision.status !== 'pending' && (
              <>
                <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px' }}>
                  <h4 style={{ marginTop: 0, color: '#333' }}>Review Details:</h4>
                  <p><strong>Reviewed by:</strong> {selectedRevision.reviewedBy?.substring(0, 12)}...</p>
                  <p><strong>Reviewed at:</strong> {selectedRevision.reviewedAt?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                  <p><strong>Notes:</strong> {selectedRevision.reviewNotes || <em style={{ color: '#999' }}>No notes</em>}</p>
                </div>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      setShowDiff(false);
                      setSelectedRevision(null);
                      setReviewNotes('');
                    }}
                    style={{
                      padding: '12px 30px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRouteRevisionsPage;
